// src/features/task/TemplateChecklistsEditor.tsx
// Editor for a TaskTemplate's default_checklists — the checklist(s) copied onto
// every task created from the template. Shared by the template create and edit
// pages.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import type { TaskChecklist } from '@core/types/task';

interface TemplateChecklistsEditorProps {
  value: TaskChecklist[];
  onChange: (checklists: TaskChecklist[]) => void;
}

function genId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function TemplateChecklistsEditor({ value, onChange }: TemplateChecklistsEditorProps) {
  const { t } = useI18n();
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newItemDraft, setNewItemDraft] = useState<Record<string, string>>({});

  const addChecklist = () => {
    if (!newChecklistTitle.trim()) return;
    onChange([
      ...value,
      { id: genId(), task_id: '', title: newChecklistTitle.trim(), items: { items: [] }, order: value.length },
    ]);
    setNewChecklistTitle('');
  };

  const removeChecklist = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const renameChecklist = (idx: number, title: string) => {
    onChange(value.map((c, i) => (i === idx ? { ...c, title } : c)));
  };

  const addItem = (idx: number) => {
    const content = (newItemDraft[value[idx].id] || '').trim();
    if (!content) return;
    const checklist = value[idx];
    const items = checklist.items?.items || [];
    onChange(value.map((c, i) => (i === idx
      ? { ...c, items: { items: [...items, { id: genId(), checklist_id: c.id, content, is_done: false, order: items.length }] } }
      : c)));
    setNewItemDraft({ ...newItemDraft, [checklist.id]: '' });
  };

  const removeItem = (checklistIdx: number, itemIdx: number) => {
    const checklist = value[checklistIdx];
    const items = (checklist.items?.items || []).filter((_, i) => i !== itemIdx);
    onChange(value.map((c, i) => (i === checklistIdx ? { ...c, items: { items } } : c)));
  };

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('task.template.noChecklists')}</p>
      ) : (
        <div className="space-y-3">
          {value.map((checklist, idx) => (
            <div key={checklist.id} className="rounded-md border border-border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  aria-label={t('task.checklistTitle')}
                  value={checklist.title}
                  onChange={(e) => renameChecklist(idx, e.target.value)}
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm font-medium text-foreground"
                />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeChecklist(idx)}>
                  {t('common.remove')}
                </Button>
              </div>
              <ul className="space-y-1 pl-1">
                {(checklist.items?.items || []).map((item, itemIdx) => (
                  <li key={item.id} className="flex items-center gap-2">
                    <span className="text-sm text-foreground flex-1">{item.content}</span>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeItem(idx, itemIdx)}>
                      {t('common.remove')}
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  aria-label={t('task.checklistItemPlaceholder')}
                  value={newItemDraft[checklist.id] || ''}
                  onChange={(e) => setNewItemDraft({ ...newItemDraft, [checklist.id]: e.target.value })}
                  placeholder={t('task.checklistItemPlaceholder')}
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
                />
                <Button size="sm" variant="secondary" onClick={() => addItem(idx)} disabled={!(newItemDraft[checklist.id] || '').trim()}>
                  {t('task.addChecklistItem')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          type="text"
          aria-label={t('task.checklistTitle')}
          value={newChecklistTitle}
          onChange={(e) => setNewChecklistTitle(e.target.value)}
          placeholder={t('task.template.newChecklistPlaceholder')}
          className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground"
        />
        <Button size="sm" variant="secondary" onClick={addChecklist} disabled={!newChecklistTitle.trim()}>
          {t('task.addChecklist')}
        </Button>
      </div>
    </div>
  );
}

export default TemplateChecklistsEditor;
